import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Button, Callout, Card, Flex, Heading, LinkButton, Text, Typewriter } from 'core/components';

import { ReactComponent as GenAiIcon } from 'app/assets/icons/journeys-icon-1.svg';
import { ReactComponent as ModernNetworksIcon } from 'app/assets/icons/journeys-icon-2.svg';
import { ReactComponent as DemocratizeIcon } from 'app/assets/icons/journeys-icon-3.svg';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import UserInitials from './UserInitials';

const Feature = ({ title, children, icon }) => (
  <Card p={3} style={{ lineHeight: '22px', minHeight: 250, minWidth: 300 }} elevation={1} flex={1}>
    <Flex flexDirection="column" gap="8px">
      {icon}
      <Heading level={5} m={0}>
        {title}
      </Heading>
      <Text muted>{children}</Text>
    </Flex>
  </Card>
);

const iconStyle = { width: 32, height: 32, marginBottom: 8 };

@withRouter
@inject('$app', '$auth', '$users')
@observer
class KaizenPaintedDoor extends Component {
  handleRequestAccess = () => {
    const { $app } = this.props;
    $app.setReportIssuePopoverPropsAndOpen({
      type: 'ai request',
      summary: 'Request to enable Kentik AI',
      description: ''
    });
  };

  render() {
    const { location, $auth, $users } = this.props;
    const { state } = location;
    const { companyHasSuperAdmins } = $users;

    return (
      <Flex flexDirection="column" pt="40px" pb="24px" m="0px auto" maxWidth={950} gap="32px">
        <Flex flexDirection="column" gap="12px">
          {state?.prompt && (
            <Card p="12px" elevation={1} mb={1}>
              <Flex alignItems="center" gap={1}>
                <UserInitials userId={$auth.activeUser.id} />
                <Heading level={5} m={0} fontWeight="medium" flex={1}>
                  <Typewriter
                    removeCursor={false}
                    text={state?.prompt?.includes('?') ? state?.prompt : `${state.prompt}?`}
                    options={{ autoStart: true, loop: true, delay: 100, deleteSpeed: 50 }}
                  />
                </Heading>
              </Flex>
            </Card>
          )}

          {/* if user is not a super admin and there are super admins in the company */}
          {!$auth.isSuperAdministrator && companyHasSuperAdmins && (
            <Callout p="12px" intent="warning" textAlign="center" mb={2}>
              <Text fontWeight="bold">
                To access AI Advisor, please ask your company Super Admin to enable Kentik AI.
              </Text>
            </Callout>
          )}

          <Heading m={0} level={1} fontWeight={300} display="flex" justifyContent="space-between" alignItems="center">
            Unlock the Power of Kentik AI
            <Flex alignItems="center" gap={4}>
              <Flex gap={1} flexDirection="column" alignItems="center">
                {/* if user is a super admin, they can enable from Kentik AI settings */}
                {$auth.isSuperAdministrator && (
                  <LinkButton
                    to="/v4/settings/kentik-ai"
                    fontWeight="bold"
                    intent="primary"
                    large
                    style={{ padding: '8px 18px' }}
                  >
                    Enable Kentik AI
                  </LinkButton>
                )}

                {/* if user is not a super admin and there are no super admins in the company */}
                {!$auth.isSuperAdministrator && !companyHasSuperAdmins && (
                  <Button
                    onClick={this.handleRequestAccess}
                    fontWeight="bold"
                    intent="primary"
                    large
                    style={{ padding: '8px 18px' }}
                  >
                    Request Access
                  </Button>
                )}
              </Flex>
            </Flex>
          </Heading>

          <Card p={0} elevation={4}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video width="100%" style={{ width: '100%', height: 'auto' }} autoPlay controls>
              <source
                src="https://videos.ctfassets.net/6yom6slo28h2/RMNEG1sLDAcYHSShbJ2Y5/c8660056b0b36426f9882bffb364f24b/kaizen_painted_door.mp4"
                type="video/mp4"
              />
            </video>
          </Card>

          <Text as="p" large lineHeight="24px" mt={2}>
            Kentik AI transforms your network operations with intelligent automation and instant insights. As your
            dedicated AI-powered network agent, AI Advisor provides real-time analysis, proactive troubleshooting, and
            expert guidance—all through natural language conversations.
          </Text>

          <Flex alignItems="flex-start" gap={3} flexWrap="wrap">
            <Feature title="Intelligent Network Analysis" icon={<GenAiIcon style={iconStyle} />}>
              Ask questions in plain English and get instant answers about your network performance, traffic patterns,
              device health, and security threats. No complex queries or dashboard navigation required.
            </Feature>
            <Feature title="Automated Troubleshooting" icon={<ModernNetworksIcon style={iconStyle} />}>
              AI Advisor can diagnose connectivity issues, analyze traffic anomalies, investigate alerts, and provide
              step-by-step remediation guidance—dramatically reducing your mean time to resolution.
            </Feature>
            <Feature title="Comprehensive Observability" icon={<DemocratizeIcon style={iconStyle} />}>
              Access all your network data through conversational AI: flow analytics, device metrics, cloud topology,
              and more in a unified intelligent interface.
            </Feature>
          </Flex>
        </Flex>
      </Flex>
    );
  }
}

export default KaizenPaintedDoor;
